import React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import ExpandMoreSharpIcon from "@mui/icons-material/ExpandMoreSharp";
import TableRow from "@mui/material/TableRow";
import Moment from "react-moment";
import Button from '@mui/material/Button';

import {
  Column,
  Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
  flexRender,ColumnSizing
} from "@tanstack/react-table";

type Game = {
    _id:string,
    localTeamID:{
      _id:string,
      name:string,
    },
    guessTeamID:{
      _id:string,
      name:string
    },
    status:string
    date:Date,
    winner:string,
    localTeamPTS:number,
    guessTeamPTS:number
  
};

const GamesList = ({games}) => {

  const data = games;

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const columns = React.useMemo<ColumnDef<Game>[]>(
    () => [
      
          {
            accessorFn: (row) => row.date,
            header: "Date",
              
            cell: (info) => (<div className="flex flex-col">
              <Moment date={info.getValue<any>()} format={"ddd"}></Moment>
              <Moment date={info.getValue<any>()} format={"DD/MM/YYYY"}></Moment>
            </div>
            )
          },{
            accessorFn: (row) => row.status,
            header: "Status",
              
            cell: (info) => (info.getValue()!== "TBP" && info.getValue()!== "F"?(<p className="text-red-500 font-semibold">{info.getValue<any>()}</p>
            ):(<p className="font-semibold">{info.getValue<any>()}</p>)),
            enableSorting: false,
          },{
            header: "Score",
            accessorFn: (row) => `${row.localTeamPTS} ${row.guessTeamPTS}`,
            cell: (info) => (
               info.row.original.localTeamPTS + info.row.original.guessTeamPTS !== 0?(<Button variant="outlined"
               color="inherit" sx={{
                 bgcolor: "white",
                 color: "black",
                 fontWeight: "600",
                 fontFamily:"Poppins",
                 ":hover": { color: "white" },
               }} href={`/game/${info.row.original._id}`}>{info.row.original.localTeamPTS.toLocaleString()} - {info.row.original.guessTeamPTS.toLocaleString()}</Button>):(<div>-</div>)
            ),
            enableSorting: false,
            enableColumnFilter: false,
          },{
            header: "Home",
            accessorFn: (row) => row.localTeamID.name,
            cell: (info) => (
                <Button variant="outlined"
                color="inherit" sx={{
                  bgcolor: "white",
                  width: "100%",
                  color: "black",
                  fontWeight: "600",
                  fontFamily:"Poppins",
                  ":hover": { color: "white" },
                }} href={`/team/${info.row.original.localTeamID._id}`}>
              <p className={`${info.row.original.winner===info.row.original.localTeamID._id?"text-green-500 font-bold":""}`}>{info.row.original.localTeamID.name}</p>
            </Button>
            ),
            enableSorting: false,minSize:120,
          }
          ,{
            header: "Visitors",
            accessorFn: (row) => row.guessTeamID.name,
            cell: (info) => (
              <Button variant="outlined"
              color="inherit" sx={{
                bgcolor: "white",
                width: "100%",
                color: "black",
                fontWeight: "600",
                fontFamily:"Poppins",
                ":hover": { color: "white" },
              }} href={`/team/${info.row.original.guessTeamID._id}`}>
             <p className={`${info.row.original.winner===info.row.original.guessTeamID._id?"text-green-500 font-bold":""}`}>{info.row.original.guessTeamID.name}</p>
                </Button>
            ),
            enableSorting: false, minSize:120,
          },
    ],
    []
  );

  function LocalTable({
    data,
    columns,
  }: {
    data: Game[];
    columns: ColumnDef<Game>[];
  }) {
    const table = useReactTable({
      data,
      columns,
      // Pipeline
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel()
      //
    });

    return (
      <Box sx={{ width: "100%" }}>

        <TableContainer className="box">
          <div className="boxbox">
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableCell
                          key={header.id}
                          colSpan={header.colSpan}
                          sx={{ fontFamily: "Poppins", fontWeight: "bold", width: header.getSize }}
                        >
                          {header.isPlaceholder ? null : (
                            <div>
                              <div
                                {...{
                                  className: header.column.getCanSort()
                                    ? "cursor-pointer select-none flex-col flex align-center items-center sortLabel"
                                    : "inline-block flex justify-center ",
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}{" "}
                                {header.column.getCanSort() &&
                                  header.column.getIsSorted() !== "asc" &&
                                  header.column.getIsSorted() !== "desc" && (
                                    <div className={`inline-block`}>
                                      <ExpandMoreSharpIcon
                                        className={` inline-block sort sortHover`}
                                      />
                                    </div>
                                  )}
                                {(header.column.getIsSorted() === "asc" ||
                                  header.column.getIsSorted() === "desc") && (
                                  <div className={`inline-block`}>
                                    <ExpandMoreSharpIcon
                                      className={` inline-block sort ${
                                        header.column.getIsSorted() === "asc"
                                          ? "sortFlip"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody >
                {table.getRowModel().rows.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell
                            key={cell.id}
                            sx={{ textAlign: "center", fontFamily: "Poppins",  padding:"4px", userSelect:"none"}}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TableContainer>
      </Box>
    );
  }

  return (
    <>
      <LocalTable {...{ data, columns }} />
    </>
  );
};

export default GamesList;